import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import { Button } from "@mui/material";
import { ShippingDocumentKind } from "generated-graphql/graphql";
import { useCallback, useMemo } from "react";

import { applyShowFilterToSearch } from "./manifestShowFilters";
import { withKindTerm } from "./shippingPaperworkKindFilter";
import { useShowFilterCounts } from "./useShowFilterCounts";

function needsReviewLabel(count: number, kind: ShippingDocumentKind | undefined): string {
  if (kind === ShippingDocumentKind.BillOfLading) {
    return count === 1 ? "1 bill of lading needs review" : `${count} bills of lading need review`;
  }
  return count === 1 ? "1 manifest needs review" : `${count} manifests need review`;
}

/**
 * How many manifests are waiting on a review, said on the page rather than
 * inside a dropdown.
 *
 * The count already existed as an option label in the Show picker, which meant a
 * reviewer had to suspect there was work and go looking for it before the page
 * would admit it. Now that customers review their own manifest documents, "is
 * there anything to do here" is the first question the screen should answer.
 *
 * Renders nothing at zero: a page with no outstanding review should look like a
 * page with nothing to do, and a standing "0 need review" is the kind of chrome
 * people stop reading.
 *
 * With a `kind`, the count and the filter both narrow to that kind, so a bill of
 * lading is never counted as a manifest. Without one the prompt is unchanged.
 *
 * A button rather than a chip on purpose — the design system's filter-chip
 * selection pattern is still exploring, so this is deliberately not built to it.
 */
export function NeedsReviewPrompt({
  defaultSearch,
  omnisearch,
  onApply,
  kind,
}: {
  /** The page scope the counts are taken against (tenant, and the year picker). */
  defaultSearch: string;
  /** The active omnisearch string, which applying the preset rewrites. */
  omnisearch: string;
  onApply: (nextOmnisearch: string) => void;
  /** Counts and filters only this kind of shipping document. */
  kind?: ShippingDocumentKind;
}) {
  const scopedSearch = useMemo(
    () => (kind ? withKindTerm(defaultSearch, kind) : defaultSearch),
    [defaultSearch, kind]
  );
  const { countByFilterId } = useShowFilterCounts(scopedSearch);
  const count = countByFilterId.get("needsDocumentReview") ?? 0;

  const handleClick = useCallback(() => {
    const preset = applyShowFilterToSearch(omnisearch, "needsDocumentReview");
    onApply(kind ? withKindTerm(preset, kind) : preset);
  }, [omnisearch, onApply, kind]);

  if (count === 0) return null;

  return (
    <Button
      size="small"
      color="primary"
      startIcon={<RateReviewOutlined fontSize="small" />}
      onClick={handleClick}
    >
      {needsReviewLabel(count, kind)}
    </Button>
  );
}
